import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { apiClient } from '../api/apiClient';

// Mapping grupo Entra ID → rol(es) ACL
const GROUP_ROLE_MAP: Record<string, string[]> = {
  '7d434913-f1c0-4b4a-8b49-5f4b053437f4': ['Administracion'],
  'a66787cc-d705-42b2-bc52-1389c934bb5c': ['Administrador', 'Administrador Gescom'],
  '75f32c4f-d280-4d19-8f47-bf97649857f6': ['Asesor Patrimonial'],
  '9e744048-a2e7-492b-86df-79ea182de550': ['Comite Direccion'],
  '0baece31-d735-41ea-9137-d7e436938a10': ['Director Comercial'],
  '44651536-0d7b-4245-b6a4-875fcf95d7c8': ['Director Territorial'],
  '75d2d58c-4346-41b7-84f1-6121d410ca58': ['Gerente'],
  'b6d06868-2ef2-42fb-af4c-f904fe0fb050': ['Marketing'],
  '16bcfe58-0abf-4f40-a4da-2e09b3550c05': ['Solusoft'],
  'b8037fa6-e738-467a-b1d3-0842d538c6c7': ['Tecnico'],
  '5025ec3b-8a9f-47e1-8016-6a9e602004be': ['Smartup'], // Proveedor externo (invitados B2B) — PoC acceso invitados / Mejora CU2
};

const ALL_GROUP_IDS = Object.keys(GROUP_ROLE_MAP);

interface PermissionContextType {
  roles: string[];
  permissions: string[];
  isLoading: boolean;
  hasPermission: (resource: string) => boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

// Store roles for the axios interceptor to read
let currentRoles: string[] = [];
export const getCurrentRoles = (): string[] => currentRoles;

export const PermissionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, getAccessToken } = useAuth();
  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const resolvePermissions = useCallback(async () => {
    if (!isAuthenticated || authLoading) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Step 1: Get Graph token and check group membership
      const token = await getAccessToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      const graphResponse = await fetch(
        'https://graph.microsoft.com/v1.0/me/checkMemberGroups',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ groupIds: ALL_GROUP_IDS }),
        }
      );

      if (!graphResponse.ok) {
        console.error('Error checking group membership:', graphResponse.status);
        setIsLoading(false);
        return;
      }

      const graphData = await graphResponse.json();
      const memberGroupIds: string[] = graphData.value || [];

      // Step 2: Map group IDs to roles
      const resolvedRoles: string[] = [];
      for (const groupId of memberGroupIds) {
        const mappedRoles = GROUP_ROLE_MAP[groupId];
        if (mappedRoles) {
          resolvedRoles.push(...mappedRoles);
        }
      }
      const uniqueRoles = Array.from(new Set(resolvedRoles));

      setRoles(uniqueRoles);
      currentRoles = uniqueRoles;

      if (uniqueRoles.length === 0) {
        setPermissions([]);
        setIsLoading(false);
        return;
      }

      // Step 3: Call backend to resolve resources
      const permResponse = await apiClient.post('/v1/auth/permisos', {
        roles: uniqueRoles,
      });

      const resources: string[] = permResponse.data.resources || [];
      setPermissions(resources);
    } catch (error) {
      console.error('Error resolving permissions:', error);
      setPermissions([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, authLoading, getAccessToken]);

  useEffect(() => {
    resolvePermissions();
  }, [resolvePermissions]);

  const hasPermission = useCallback(
    (resource: string): boolean => permissions.includes(resource),
    [permissions]
  );

  const value: PermissionContextType = {
    roles,
    permissions,
    isLoading,
    hasPermission,
  };

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
